import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserShield, FaClipboardList, FaUsers, FaUpload, FaFolderOpen, FaSignOutAlt, FaHourglassHalf, FaQuestion, FaSpinner, FaBell } from 'react-icons/fa';
import { UserContext } from '../userContext.js';
import { auth, db } from '../firebaseConfig.js';
import { signOut } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import './AdminPanel.css';

function AdminPanel() {
  const navigate = useNavigate();
  const { setUser, user } = useContext(UserContext);
  
  // Estados para contadores
  const [contadores, setContadores] = useState({
    materiaisAguardando: 0,
    perguntasNovas: 0,
    perguntasNaoLidas: 0
  });
  const [carregandoContadores, setCarregandoContadores] = useState(true);

  // Verificar se é especialista (role pode vir do contexto ou banco)
  const isEspecialista = user?.role === 'especialista';
  const isAdmin = user?.role === 'admin' || !user?.role; // fallback para admin se não tiver role definido

  const carregarContadores = useCallback(async () => {
    try {
      // Contar materiais aguardando aprovação
      const materiaisQuery = query(
        collection(db, 'student_materials'), 
        where('status', '==', 'pendente')
      );
      const materiaisSnapshot = await getDocs(materiaisQuery);
      const materiaisAguardando = materiaisSnapshot.size;

      // Contar perguntas novas
      const perguntasNovasQuery = query(
        collection(db, 'perguntas_alunos'), 
        where('status', '==', 'nova')
      );
      const perguntasNovasSnapshot = await getDocs(perguntasNovasQuery);
      const perguntasNovas = perguntasNovasSnapshot.size;

      // Contar perguntas não lidas
      const perguntasNaoLidasQuery = query(
        collection(db, 'perguntas_alunos'), 
        where('lida', '==', false)
      );
      const perguntasNaoLidasSnapshot = await getDocs(perguntasNaoLidasQuery);
      const perguntasNaoLidas = perguntasNaoLidasSnapshot.size;

      setContadores({
        materiaisAguardando,
        perguntasNovas,
        perguntasNaoLidas
      });
    } catch (error) {
      console.error('Erro ao carregar contadores:', error);
    } finally {
      setCarregandoContadores(false);
    }
  }, []);

  // Carregar contadores ao montar e reduzir polling em aba oculta
  useEffect(() => {
    const runIfVisible = () => {
      if (document.visibilityState === 'visible') {
        carregarContadores();
      }
    };

    runIfVisible();
    const interval = setInterval(runIfVisible, 120000);
    document.addEventListener('visibilitychange', runIfVisible);
    window.addEventListener('focus', runIfVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', runIfVisible);
      window.removeEventListener('focus', runIfVisible);
    };
  }, [carregarContadores]);

  const handleLogout = async () => {
    try {
      await signOut(auth); // Desloga o usuário do Firebase Auth
      setUser(null); // Limpa o estado do usuário no UserContext
      navigate('/admin-login'); // Redireciona para a página de login
    } catch (error) {
      console.error('Erro ao deslogar:', error);
    }
  };

  return (
    <div className="admin-home-container">
      <header className="admin-home-header">
        <div className="header-content">
          <h1>
            {isEspecialista ? 'Bem-vindo, Especialista!' : 'Bem-vindo, Administrador!'}
          </h1>
          <p>
            {isEspecialista 
              ? 'Gerencie materiais e responda perguntas dos alunos.'
              : 'Escolha uma opção abaixo para começar a gerenciar o sistema.'
            }
          </p>
          
          {/* Indicador de carregamento dos contadores */}
          {carregandoContadores && (
            <div className="contadores-loading">
              <FaSpinner className="spinner" />
              <span>Carregando notificações...</span>
            </div>
          )}
        </div>
        <button onClick={handleLogout} className="logout-button">
          <FaSignOutAlt className="admin-icon" />
          <span>Sair</span>
        </button>
      </header>

      <div className="admin-buttons-grid">
        {/* Botões apenas para Admins */}
        {isAdmin && (
          <>
            <button onClick={() => navigate('/admin/administradores')} className="admin-button">
              <FaUserShield className="admin-icon" />
              <span>Gerenciar Administradores e Especialistas</span>
            </button>

            <button onClick={() => navigate('/admin/planos')} className="admin-button">
              <FaClipboardList className="admin-icon" />
              <span>Gerenciar Planos</span>
            </button>

            <button onClick={() => navigate('/admin/usuarios')} className="admin-button">
              <FaUsers className="admin-icon" />
              <span>Gerenciar Usuários</span>
            </button>
          </>
        )}

        {/* Botões para Admins e Especialistas */}
        <button onClick={() => navigate('/admin/materiais')} className="admin-button">
          <FaUpload className="admin-icon" />
          <span>Subir/Gerenciar Materiais</span>
        </button>

        <button 
          onClick={() => navigate('/admin/materiais-aprovacao')} 
          className={`admin-button ${contadores.materiaisAguardando > 0 ? 'has-notification' : ''}`}
        >
          <FaHourglassHalf className="admin-icon" />
          <div className="button-content">
            <span>Material Aguardando Aprovação</span>
            {!carregandoContadores && contadores.materiaisAguardando > 0 && (
              <div className="contador-badge">
                {contadores.materiaisAguardando}
              </div>
            )}
          </div>
        </button>

        <button 
          onClick={() => navigate('/admin/perguntas-alunos')} 
          className={`admin-button ${(contadores.perguntasNovas > 0 || contadores.perguntasNaoLidas > 0) ? 'has-notification' : ''}`}
        >
          <FaQuestion className="admin-icon" />
          <div className="button-content">
            <span>Perguntas dos Alunos</span>
            {!carregandoContadores && (contadores.perguntasNovas > 0 || contadores.perguntasNaoLidas > 0) && (
              <div className="contador-badge">
                {Math.max(contadores.perguntasNovas, contadores.perguntasNaoLidas)}
              </div>
            )}
          </div>
        </button>

        {/* Botões apenas para Admins */}
        {isAdmin && (
          <>
            <button onClick={() => navigate('/admin/avisos')} className="admin-button">
              <FaBell className="admin-icon" />
              <span>Gerenciar Avisos</span>
            </button>

            <button onClick={() => navigate('/admin/categorias')} className="admin-button">
              <FaFolderOpen className="admin-icon" />
              <span>Categorias/Subcategorias</span>
            </button>
          </>
        )}
      </div>



      {/* Informação adicional para Especialistas */}
      {isEspecialista && (
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'rgba(52, 152, 219, 0.1)',
          border: '1px solid rgba(52, 152, 219, 0.2)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#3498db', marginBottom: '0.5rem' }}>✓ Perfil Especialista</h3>
          <p style={{ color: '#6c757d', margin: 0 }}>
            Seus materiais são marcados como "Verificados" e você tem acesso total à plataforma.
          </p>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
